/**
 * SessionStorage Utility Functions
 * Provides global functions for managing sessionStorage with error handling and type safety
 */


/**
 * Set an item in sessionStorage
 * @param {string} key - The key to store the value under
 * @param {any} value - The value to store (will be JSON stringified)
 * @returns {boolean} True if successful, false otherwise
 */
export const setSessionItem = (key, value) => {
  try {
    const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
    sessionStorage.setItem(key, stringValue);
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Get an item from sessionStorage
 * @param {string} key - The key to retrieve
 * @param {any} defaultValue - Default value to return if key doesn't exist
 * @returns {any} The stored value or default value
 */
export const getSessionItem = (key, defaultValue = null) => {
  try {
    const item = sessionStorage.getItem(key);
    if (item === null) {
      return defaultValue;
    }

    // Try to parse as JSON, if it fails return the raw string
    try {
      return JSON.parse(item);
    } catch {
      return item;
    }
  } catch (error) {
    return defaultValue;
  }
};

/**
 * Remove an item from sessionStorage
 * @param {string} key - The key to remove
 * @returns {boolean} True if successful, false otherwise
 */
export const removeSessionItem = (key) => {
  try {
    sessionStorage.removeItem(key);
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Clear all items from sessionStorage
 * @returns {boolean} True if successful, false otherwise
 */
export const clearSessionStorage = () => {
  try {
    sessionStorage.clear();
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Get all keys from sessionStorage
 * @returns {string[]} Array of all keys in sessionStorage
 */
export const getSessionKeys = () => {
  try {
    return Object.keys(sessionStorage);
  } catch (error) {
    return [];
  }
};

/**
 * Get the number of items in sessionStorage
 * @returns {number} Number of items in sessionStorage
 */
export const getSessionLength = () => {
  try {
    return sessionStorage.length;
  } catch (error) {
    return 0;
  }
};

/**
 * Check if a key exists in sessionStorage
 * @param {string} key - The key to check
 * @returns {boolean} True if key exists, false otherwise
 */
export const hasSessionKey = (key) => {
  try {
    return sessionStorage.getItem(key) !== null;
  } catch (error) {
    return false;
  }
};

/**
 * Get all items from sessionStorage as an object
 * @returns {Object} Object containing all sessionStorage key-value pairs
 */
export const getAllSessionItems = () => {
  try {
    const items = {};
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key) {
        items[key] = getSessionItem(key);
      }
    }
    return items;
  } catch (error) {
    return {};
  }
};

/**
 * Set multiple items in sessionStorage at once
 * @param {Object} items - Object containing key-value pairs to store
 * @returns {boolean} True if all items were set successfully, false otherwise
 */
export const setMultipleSessionItems = (items) => {
  try {
    const keys = Object.keys(items);
    let allSuccess = true;

    keys.forEach(key => {
      const success = setSessionItem(key, items[key]);
      if (!success) {
        allSuccess = false;
      }
    });

    return allSuccess;
  } catch (error) {
    return false;
  }
};

/**
 * Remove multiple items from sessionStorage at once
 * @param {string[]} keys - Array of keys to remove
 * @returns {boolean} True if all items were removed successfully, false otherwise
 */
export const removeMultipleSessionItems = (keys) => {
  try {
    let allSuccess = true;

    keys.forEach(key => {
      const success = removeSessionItem(key);
      if (!success) {
        allSuccess = false;
      }
    });

    return allSuccess;
  } catch (error) {
    return false;
  }
};

// Make functions available globally
if (typeof window !== 'undefined') {
  window.sessionStorageUtils = {
    set: setSessionItem,
    get: getSessionItem,
    remove: removeSessionItem,
    clear: clearSessionStorage,
    keys: getSessionKeys,
    length: getSessionLength,
    has: hasSessionKey,
    getAll: getAllSessionItems,
    setMultiple: setMultipleSessionItems,
    removeMultiple: removeMultipleSessionItems
  };
}

// Default export with all functions
export default {
  set: setSessionItem,
  get: getSessionItem,
  remove: removeSessionItem,
  clear: clearSessionStorage,
  keys: getSessionKeys,
  length: getSessionLength,
  has: hasSessionKey,
  getAll: getAllSessionItems,
  setMultiple: setMultipleSessionItems,
  removeMultiple: removeMultipleSessionItems
};
